import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../lib/convexApi";
import { settingsStore } from "../stores/settingsStore";

export type NotificationKind = "response-ready" | "cli-idle" | "approval-needed";

export interface NotificationPayload {
  kind: NotificationKind;
  source: { type: "chat" | "terminal"; id: string; label: string };
  preview: string;
  onClickPath?: string;
  ts?: number;
}

const MUTE_KEY = "agentguard-notifications-muted";
const CHIME_PATH = "/sounds/chime.wav";

// Tauri v2 exposes plugins as separate npm packages, not as `window.__TAURI__`
// globals. The check below works whether we're in Tauri (the import succeeds
// and the IPC bridge is present) or a plain browser (the import resolves but
// calling sendNotification throws — caught and falls back to Web Notifications).
function isLikelyTauri(): boolean {
  // __TAURI_INTERNALS__ is set by Tauri's IPC shim. Cheaper than a dynamic
  // import probe and reliable on both v1 and v2 release builds.
  return (
    typeof window !== "undefined" &&
    Boolean((window as unknown as { __TAURI_INTERNALS__?: unknown }).__TAURI_INTERNALS__)
  );
}

class NotificationService {
  // Web Audio API path: decode the WAV once into an AudioBuffer, then play via
  // a fresh AudioBufferSourceNode each chime. This is required because Tauri's
  // wry WKWebView rejects HTMLAudioElement.play() with NotSupportedError —
  // wry doesn't link the AVKit media plumbing that <audio> relies on, but the
  // Web Audio API path goes through a different lower-level decoder that
  // wry's WKWebView fully supports. See devtools error from notifications.ts:90.
  private audioContext: AudioContext | null = null;
  private chimeBuffer: AudioBuffer | null = null;
  private chimeLoading: Promise<void> | null = null;

  constructor() {
    if (typeof window !== "undefined") {
      // Defer AudioContext construction until first user gesture — most
      // platforms now start contexts in "suspended" state until then.
      const unlock = () => {
        if (!this.audioContext) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const Ctor: typeof AudioContext =
            window.AudioContext || (window as any).webkitAudioContext;
          if (!Ctor) return;
          try {
            this.audioContext = new Ctor();
          } catch (err) {
            console.warn("[notifications] AudioContext construction failed", err);
            return;
          }
        }
        // resume() must be called from a user-gesture stack frame to count.
        // We hold no flag — calling it repeatedly is cheap and idempotent.
        void this.audioContext.resume();
        // Kick off (or re-await) the chime decode in the background.
        void this.ensureChimeLoaded();
      };
      window.addEventListener("pointerdown", unlock, { capture: true });
      window.addEventListener("keydown", unlock, { capture: true });
    }
  }

  isMuted(): boolean {
    return localStorage.getItem(MUTE_KEY) === "1";
  }

  setMuted(muted: boolean): void {
    if (muted) localStorage.setItem(MUTE_KEY, "1");
    else localStorage.removeItem(MUTE_KEY);
  }

  show(payload: NotificationPayload): void {
    if (this.isMuted()) return;

    // Respect Settings → Notifications panel preferences. The legacy
    // localStorage mute (isMuted) is a one-click quick-silence used by
    // NotificationToggle.tsx; the server-persisted settings are the
    // master / per-trigger / chime controls from the Settings modal.
    // During the pre-load window settingsStore returns INITIAL_SETTINGS
    // with everything enabled, so behavior matches the old "always-on" default.
    const { notifications } = settingsStore.getState().settings;
    if (!notifications.enabled) return;
    if (notifications.triggers[payload.kind] === false) return;

    // "Right time" gate: a notification you're already looking at is just
    // noise. If the app window is foreground AND visible, the user can
    // already see the chat/terminal update — so skip the audible chime and
    // the OS banner. (You still get the in-app dispatch + the unread badge,
    // which are driven off the reactive inbox query regardless.) When the
    // app is backgrounded or another window has focus, fire the full
    // notification — that's the only time it actually carries information.
    // This is what stops "spam for no reason" while you're working in-app.
    const appForeground =
      typeof document !== "undefined" &&
      document.visibilityState === "visible" &&
      (typeof document.hasFocus !== "function" || document.hasFocus());

    // Fire async side-effects without blocking; in-app dispatch is intentionally
    // synchronous so the UI reacts immediately regardless of audio/OS latency.
    if (!appForeground) {
      if (notifications.chime) void this.playChime();
      void this.sendOsNotification(payload);
    }
    this.dispatchInApp(payload);
  }

  private async ensureChimeLoaded(): Promise<void> {
    if (this.chimeBuffer || !this.audioContext) return;
    if (this.chimeLoading) return this.chimeLoading;
    const ctx = this.audioContext;
    this.chimeLoading = (async () => {
      const res = await fetch(CHIME_PATH);
      if (!res.ok) throw new Error(`chime fetch ${res.status}`);
      const arrayBuffer = await res.arrayBuffer();
      this.chimeBuffer = await ctx.decodeAudioData(arrayBuffer);
    })();
    try {
      await this.chimeLoading;
    } finally {
      // Clear the inflight so a future caller retries the fetch on failure.
      if (!this.chimeBuffer) this.chimeLoading = null;
    }
  }

  private async playChime(): Promise<void> {
    try {
      if (!this.audioContext) {
        // User hasn't interacted yet — no AudioContext exists. Browsers won't
        // let us create one outside a gesture, so just degrade silently.
        console.warn("[notifications] playChime skipped: no AudioContext (no user gesture yet)");
        return;
      }
      const ctx = this.audioContext;
      if (ctx.state === "suspended") {
        await ctx.resume();
      }
      await this.ensureChimeLoaded();
      if (!this.chimeBuffer) return;
      const source = ctx.createBufferSource();
      source.buffer = this.chimeBuffer;
      source.connect(ctx.destination);
      source.start(0);
    } catch (err) {
      // autoplay blocked or asset missing — degrade silently in production
      if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV)
        console.warn("[notifications] playChime failed", err);
    }
  }

  private async sendOsNotification(payload: NotificationPayload): Promise<void> {
    const title = this.titleFor(payload);
    const body = payload.preview.slice(0, 120);

    // Tauri v2 plugin path — dynamic import keeps the package out of the
    // browser-only bundle when the runtime isn't Tauri.
    if (isLikelyTauri()) {
      try {
        const mod = await import("@tauri-apps/plugin-notification");
        let granted = await mod.isPermissionGranted();
        if (!granted) {
          const res = await mod.requestPermission();
          granted = res === "granted";
        }
        if (granted) {
          await mod.sendNotification({ title, body });
          return;
        }
        // Permission denied — fall through to web fallback (which will also
        // be denied, but at least it logs visibly via the Notifications API).
      } catch (err) {
        console.warn("[notifications] tauri sendNotification failed", err);
        // fall through to web fallback
      }
    }

    // Web Notifications API fallback
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "denied") return;
    if (Notification.permission === "default") {
      const res = await Notification.requestPermission();
      if (res !== "granted") return;
    }
    new Notification(title, { body });
  }

  private dispatchInApp(payload: NotificationPayload): void {
    window.dispatchEvent(new CustomEvent("agentguard:notification", { detail: payload }));
  }

  private titleFor(payload: NotificationPayload): string {
    switch (payload.kind) {
      case "response-ready":
        return `${payload.source.label} responded`;
      case "cli-idle":
        return `${payload.source.label} is idle`;
      case "approval-needed":
        return `${payload.source.label} needs approval`;
    }
  }
}

export const notifyService = new NotificationService();

// ── Convex reactive integration ─────────────────────────────────────────
// `useNotifications()` is the new data source: it subscribes to the
// per-user `notifications.queries.inbox` query so the inbox + badge stay
// live without an SSE bridge. The legacy `notifyService` (audio + Tauri
// OS notification) is preserved and reused — the hook detects newly
// arrived unread rows and fires `notifyService.show(...)` for the side
// effects, so chime, settings gating, and the Tauri plugin path keep
// working unchanged.
//
// Mute is stored in `agentguard-notifications-muted` (the same key
// `NotificationService.isMuted/setMuted` use), so toggling from the
// hook and from the imperative API stay in sync.

export interface NotificationRow {
  _id: string;
  kind: NotificationKind;
  title: string;
  body?: string;
  href?: string;
  readAt?: number;
  createdAt: number;
}

export interface UseNotificationsResult {
  inbox: NotificationRow[];
  unreadCount: number;
  muted: boolean;
  toggleMuted(): void;
  markRead(id: string): Promise<void>;
  markAllRead(): Promise<void>;
}

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeMuted(value: boolean): void {
  try {
    if (value) localStorage.setItem(MUTE_KEY, "1");
    else localStorage.removeItem(MUTE_KEY);
  } catch {
    // localStorage unavailable (private mode); no-op.
  }
}

export interface UseNotificationsOptions {
  // When true, this hook instance fires chime + OS notification side
  // effects on each new unread row. Set on EXACTLY ONE consumer (the
  // top-level <App /> mount); every other consumer (e.g.
  // NotificationToggle in the topbar) should pass false / omit so
  // they only read shared state. Codex PR #55 C55-2: prior to this
  // option every useNotifications() call had its own seenIds + ran
  // notifyService.show(), causing duplicate chimes once the topbar
  // mounted.
  subscribe?: boolean;
}

export function useNotifications(opts: UseNotificationsOptions = {}): UseNotificationsResult {
  const { subscribe = false } = opts;
  // useQuery returns `undefined` while loading; map to [] so the rest of
  // the hook can treat it as a normal array. `as any` keeps TS happy
  // against the hand-authored anyApi shim — real codegen will type this.
  const raw = useQuery((api as any).notifications.queries.inbox, { limit: 50 });
  const inbox: NotificationRow[] = (raw as NotificationRow[] | undefined) ?? [];

  // unreadCount comes from the dedicated server-side query — deriving it
  // from `inbox` is wrong because inbox is capped at 50 rows, so a user
  // with >50 unread notifications would see the badge max out at 50
  // (Gemini PR #55 review). The query uses by_user_unread = [orgId,
  // userId, unread] for an O(unread) lookup.
  const unreadCountRaw = useQuery((api as any).notifications.queries.unreadCount, {});
  const unreadCount = typeof unreadCountRaw === "number" ? unreadCountRaw : 0;

  const [muted, setMuted] = useState<boolean>(readMuted());
  const toggleMuted = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      writeMuted(next);
      // Keep the imperative notifyService in sync so any code path that
      // still calls notifyService.show() respects the new state.
      notifyService.setMuted(next);
      return next;
    });
  }, []);

  const markReadMut = useMutation((api as any).notifications.mutations.markRead);
  const markAllReadMut = useMutation((api as any).notifications.mutations.markAllRead);

  // Track which row ids we've already chimed for so a re-render (or a
  // markRead patch arriving via Convex) doesn't re-trigger the chime.
  // The set is bounded to MAX_SEEN_IDS so a long-running session can't
  // grow it without bound (Gemini PR #55 review). When at capacity we
  // drop the oldest entry on insert — chimes for very-old notifications
  // staying around longer than that window will re-fire, which is
  // acceptable for a UX chime.
  // mountedRef guards every setState AND every side-effect schedule per
  // [[feedback-pr47-second-pass-patterns]] #24.
  const MAX_SEEN_IDS = 500;
  // Rows older than this never chime/banner — they're backlog, not events.
  // Kept generous (15 min) so a client clock skewed ahead of the server
  // doesn't silently drop genuinely-fresh notifications; still small enough
  // to suppress sign-in / remount backlog dumps.
  const NOTIFY_FRESH_MS = 15 * 60_000;
  const seenIds = useRef<Set<string>>(new Set());
  const seenOrder = useRef<string[]>([]);
  const rememberSeen = useCallback((id: string) => {
    if (seenIds.current.has(id)) return;
    seenIds.current.add(id);
    seenOrder.current.push(id);
    while (seenOrder.current.length > MAX_SEEN_IDS) {
      const dropped = seenOrder.current.shift()!;
      seenIds.current.delete(dropped);
    }
  }, []);
  const primedRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!mountedRef.current) return;
    // Skip side-effects entirely when this hook instance isn't the
    // designated subscriber (Codex PR #55 C55-2). Only ONE instance —
    // the <App /> mount — sets subscribe:true.
    if (!subscribe) return;
    // Prime on the first LOADED payload: record current ids without firing
    // any notification, so reloading the page with 20 unread rows doesn't
    // dump 20 chimes. New rows arriving AFTER prime fire normally.
    // Critically, do NOT prime while useQuery is still loading (raw ===
    // undefined → inbox []): priming on the empty placeholder marked zero
    // rows as seen, so the real first payload one render later fired a
    // banner + chime for EVERY backlogged unread row — the "hundreds of
    // notifications on sign-in" spam.
    if (!primedRef.current) {
      if (raw === undefined) return;
      for (const r of inbox) rememberSeen(r._id);
      primedRef.current = true;
      return;
    }
    if (muted) {
      for (const r of inbox) rememberSeen(r._id);
      return;
    }
    for (const r of inbox) {
      if (seenIds.current.has(r._id)) continue;
      rememberSeen(r._id);
      if (r.readAt) continue; // arrived already-read (race); no chime
      // Belt-and-braces against backlog dumps (sign-in races, App
      // remounts resetting primedRef, Convex resubscribes): a row only
      // deserves a real-time interruption when it's actually recent.
      // Older unread rows still land in the inbox + unread badge.
      if (Date.now() - r.createdAt > NOTIFY_FRESH_MS) continue;
      // Map kind → source.type. Codex PR #55 C55-5: previously
      // hard-coded "chat" for every kind, so cli-idle events were
      // misclassified as chat by downstream chatsStore.markUnread
      // logic. approval-needed stays mapped to "chat" because today's
      // NotificationPayload.source.type union is only chat|terminal —
      // approvals route through chat-context href so chat is the
      // correct subject. A dedicated "approval" source.type would
      // require extending NotificationPayload + every consumer; deferred.
      const sourceType: "chat" | "terminal" = r.kind === "cli-idle" ? "terminal" : "chat";
      // Extract the SUBJECT id from href (e.g. "/chat/abc123" →
      // "abc123"), not the notification document id. Codex PR #55
      // C55-8: chatsStore.markUnread(detail.source.id) was being
      // called with the notification _id, so response-ready events
      // could never mark the underlying chat unread.
      const subjectId = (() => {
        const href = r.href ?? "";
        const m = href.match(/^\/(?:chat|terminal|approval)s?\/([^/?#]+)/);
        return m ? m[1] : r._id;
      })();
      // Delegate to the existing imperative service so chime + Tauri OS
      // notification + settings-gating all still apply.
      notifyService.show({
        kind: r.kind,
        source: { type: sourceType, id: subjectId, label: r.title },
        preview: r.body ?? r.title,
        onClickPath: r.href,
        ts: r.createdAt,
      });
    }
  }, [inbox, muted, rememberSeen, subscribe]);

  return {
    inbox,
    unreadCount,
    muted,
    toggleMuted,
    markRead: async (id: string) => {
      await markReadMut({ id: id as never });
    },
    markAllRead: async () => {
      // The mutation paginates (MARK_ALL_READ_PAGE=500 in
      // convex/notifications/mutations.ts) and returns `{ done }`.
      // Loop until done so a user with >500 unread rows actually
      // gets all of them marked (Codex PR #55 C55-4 — was previously
      // only marking the first page and leaving the rest unread).
      // Cap the loop at MAX_PAGES so a pathological backlog or a
      // server bug returning done:false forever can't hang the UI.
      const MAX_PAGES = 50;
      for (let i = 0; i < MAX_PAGES; i++) {
        const res = await markAllReadMut({});
        if (!res || (res as { done?: boolean }).done) return;
      }
    },
  };
}
